import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  getSessionToken,
  getStoreInfo,
  getUserAdminInfo,
} from "@tiendanube/nexo";

import { exchangeNexoSessionToken, fetchBootstrap } from "./api";
import { DashboardSkeleton } from "./Skeleton";
import { FullPageState } from "./Dashboard";
import nexo from "./NexoClient";
import { useSessionStore } from "./session";

/**
 * Sync the authoritative billing-enabled flag from the backend bootstrap into
 * the session store once a session is ready. Reads the shared `["bootstrap"]`
 * query (no extra fetch), so every billing surface (Facturación tab, pricing,
 * hire copy) reflects the BACKEND's `billing_configured()` — an unconfigured
 * backend hides all billing UI regardless of the build-time flag.
 */
function BillingEnabledSync() {
  const accessToken = useSessionStore((s) => s.accessToken);
  const setBillingEnabled = useSessionStore((s) => s.setBillingEnabled);
  const { data } = useQuery({
    queryKey: ["bootstrap"],
    queryFn: () => fetchBootstrap(accessToken),
    enabled: Boolean(accessToken),
    staleTime: 60 * 1000,
  });
  useEffect(() => {
    if (data) setBillingEnabled(Boolean(data.billing_enabled));
  }, [data, setBillingEnabled]);
  return null;
}

/**
 * SessionGate — top-level session bootstrap so deep links work.
 *
 * Without this, only the Dashboard route exchanged the Nexo session token
 * for an app JWT; deep-linking to `/agents/:id/history` (or any non-root
 * route) caused queries to fire without a token and the backend returned
 * 401. SessionGate runs the exchange once on mount and gates every route
 * behind a skeleton until the JWT is in the session store.
 *
 * The dev-bypass path (`VITE_DEV_ACCESS_TOKEN`) is handled at the App
 * level before SessionGate ever renders — the token is pre-populated and
 * this component returns its children immediately.
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const { accessToken, setAccessToken } = useSessionStore();
  const [status, setStatus] = useState<
    "exchanging" | "ready" | "needs-session"
  >(accessToken ? "ready" : "exchanging");
  const [error, setError] = useState("");
  const didExchange = useRef(false);

  useEffect(() => {
    if (accessToken) {
      setStatus("ready");
      return;
    }
    if (didExchange.current) return;
    didExchange.current = true;

    async function exchange() {
      try {
        const sessionToken = await getSessionToken(nexo);
        if (!sessionToken) {
          setStatus("needs-session");
          return;
        }
        const [storeInfo, userInfo] = await Promise.all([
          getStoreInfo(nexo).catch(() => undefined),
          getUserAdminInfo(nexo).catch(() => undefined),
        ]);
        const token = await exchangeNexoSessionToken(
          sessionToken,
          storeInfo,
          userInfo,
        );
        setAccessToken(token);
        setStatus("ready");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Error de sesión");
        setStatus("needs-session");
      }
    }
    void exchange();
  }, [accessToken, setAccessToken]);

  if (status === "exchanging") {
    return <DashboardSkeleton />;
  }

  if (!accessToken || status === "needs-session") {
    return (
      <FullPageState
        title="No pudimos iniciar sesión"
        body={error || "Volvé a abrir la app desde el admin de TiendaNube."}
      />
    );
  }

  return (
    <>
      <BillingEnabledSync />
      {children}
    </>
  );
}
